import React, { useMemo, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaArrowRight } from "react-icons/fa6";
import {
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  AreaChart, Area,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter,
  RadialBarChart, RadialBar,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";

interface ChatMarkdownProps {
  msg: {
    result?: string;
  };
  handleSourceClick: (e: React.MouseEvent, url: string) => void;
}

const addNumbersToH3 = (markdown: string) => {
  if (!markdown) return markdown;

  const lines = markdown.split("\n");
  let inCodeFence = false;
  let h3count = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.trim().startsWith("```")) {
      inCodeFence = !inCodeFence;
      continue;
    }

    if (inCodeFence) continue;

    const m = line.match(/^(\s*###\s+)(.*)$/);
    if (m) {
      const prefix = m[1];
      const rest = m[2];

      if (/^\s*\d+\.\s+/.test(rest)) continue;

      h3count += 1;
      lines[i] = `${prefix}${h3count}. ${rest}`;
    }
  }

  return lines.join("\n");
};

const extractLeadingNumber = (children: ReactNode) => {
  const arr = React.Children.toArray(children);

  for (let i = 0; i < arr.length; i++) {
    const child = arr[i];
    if (typeof child === "string") {
      const m = child.match(/^\s*(\d+)\.\s*/);
      if (m) {
        const num = m[1];
        arr[i] = child.replace(/^\s*\d+\.\s*/, "");
        return { number: num, newChildren: arr };
      }
    }

 if (React.isValidElement(child)) {
  // Cast child to ReactElement with unknown props
  const element = child as React.ReactElement<{ children?: ReactNode }>;

  const childChildren = element.props.children;

  if (typeof childChildren === "string") {
    const m = childChildren.match(/^\s*(\d+)\.\s*/);
    if (m) {
      const num = m[1];
      const newChild = React.cloneElement(element, {
        ...element.props,
        children: childChildren.replace(/^\s*\d+\.\s*/, ""),
      });
      arr[i] = newChild;
      return { number: num, newChildren: arr };
    }
  }

  if (Array.isArray(childChildren)) {
    const newChildChildren = React.Children.map(childChildren, (cc) => {
      if (typeof cc === "string") {
        return cc.replace(/^\s*\d+\.\s*/, "");
      }
      return cc;
    });
    arr[i] = React.cloneElement(element, { ...element.props, children: newChildChildren });
  }
}



  }

  return { number: null, newChildren: arr };
};

// Helper to parse chart markdown blocks
function parseChartMarkdown(markdown: string) {
  const chartRegex = /:::chart\s+type=(\w+)\s*([\s\S]*?):::/g;
  let match: RegExpExecArray | null;
  const charts: { type: string; data: any; start: number; end: number }[] = [];
  while ((match = chartRegex.exec(markdown)) !== null) {
    try {
      const type = match[1];
      const dataStr = match[2];
      const data = JSON.parse(dataStr.trim());
      charts.push({ type, data, start: match.index, end: chartRegex.lastIndex });
    } catch {
      // Ignore parse errors
    }
  }
  return charts;
}

const COLORS = ["#B89B5F", "#2F486F", "#A18A49", "#E6D9B3", "#7a6e47", "#8EC0E4", "#D9A15B"];

function ChartRenderer({ type, data }: { type: string; data: any }) {
  if (!data) return null;

  switch (type) {
    case "bar":
      return (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data.labels.map((label: string, i: number) => ({
              name: label,
              ...Object.fromEntries(data.datasets.map((d: any) => [d.label, d.data[i]])),
            }))}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            {data.datasets.map((d: any, idx: number) => (
              <Bar key={d.label} dataKey={d.label} fill={COLORS[idx % COLORS.length]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      );

    case "line":
      return (
        <ResponsiveContainer width="100%" height={320}>
          <LineChart
            data={data.labels.map((label: string, i: number) => ({
              name: label,
              ...Object.fromEntries(data.datasets.map((d: any) => [d.label, d.data[i]])),
            }))}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            {data.datasets.map((d: any, idx: number) => (
              <Line key={d.label} dataKey={d.label} stroke={COLORS[idx % COLORS.length]} activeDot={{ r: 8 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      );

    case "area":
      return (
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={data.labels.map((label: string, i: number) => ({
              name: label,
              ...Object.fromEntries(data.datasets.map((d: any) => [d.label, d.data[i]])),
            }))}
            margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            {data.datasets.map((d: any, idx: number) => (
              <Area
                key={d.label}
                type="monotone"
                dataKey={d.label}
                stroke={COLORS[idx % COLORS.length]}
                fill={COLORS[idx % COLORS.length]}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );

    case "pie":
      return (
        <ResponsiveContainer width="100%" height={320}>
          <PieChart>
            <Pie
              data={data.labels.map((label: string, i: number) => ({ name: label, value: data.data[i] }))}
              dataKey="value"
              nameKey="name"
              outerRadius={120}
              label
            >
              {data.labels.map((_:any, i:any) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );

    case "radar":
      // Expects { labels: [...], datasets: [{ label: string, data: [...] }] }
      return (
        <ResponsiveContainer width="100%" height={320}>
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius="80%"
            data={data.labels.map((label: string, i: number) => ({
              subject: label,
              ...Object.fromEntries(data.datasets.map((d: any) => [d.label, d.data[i]])),
            }))}
          >
            <PolarGrid />
            <PolarAngleAxis dataKey="subject" />
            <PolarRadiusAxis />
            <Tooltip />
            <Legend />
            {data.datasets.map((d: any, idx: number) => (
              <Radar
                key={d.label}
                name={d.label}
                dataKey={d.label}
                stroke={COLORS[idx % COLORS.length]}
                fill={COLORS[idx % COLORS.length]}
                fillOpacity={0.6}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      );

    case "scatter":
      // Expects { labels: [...], datasets: [{ label: string, data: [[x, y], ...] }] }
      const scatterData = data.datasets[0]?.data.map((point: [number, number], i: number) => ({
        name: data.labels[i] || `Point ${i + 1}`,
        x: point[0],
        y: point[1],
      })) || [];
      return (
        <ResponsiveContainer width="100%" height={320}>
          <ScatterChart
            margin={{ top: 20, right: 20, bottom: 10, left: 10 }}
          >
            <CartesianGrid />
            <XAxis type="number" dataKey="x" name="X" />
            <YAxis type="number" dataKey="y" name="Y" />
            <Tooltip cursor={{ strokeDasharray: "3 3" }} />
            <Legend />
            <Scatter name={data.datasets[0]?.label} data={scatterData} fill={COLORS[0]} />
          </ScatterChart>
        </ResponsiveContainer>
      );

    case "radialbar":
      // Expects { data: [{ name: string, value: number }] }
      return (
        <ResponsiveContainer width="100%" height={320}>
          <RadialBarChart innerRadius="10%" outerRadius="80%" data={data.data} startAngle={180} endAngle={-180}>
              <RadialBar label={{ position: "insideStart", fill: "#fff" }} background={true} dataKey="value" />
              <Legend iconSize={10} layout="vertical" verticalAlign="middle" />
              <Tooltip />
            </RadialBarChart>
        </ResponsiveContainer>
      );

    default:
      return <div className="text-gray-600">Unsupported chart type: {type}</div>;
  }
}


const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ msg, handleSourceClick }) => {
  if (!msg?.result) return null;

  const processedMarkdown = useMemo(() => addNumbersToH3(msg.result || ""), [msg.result]);

  const components = useMemo(
    () => ({
      a: ({ href, children }: { href?: string; children?: ReactNode }) => (
        <button
          onClick={(e) => handleSourceClick(e, href || "")}
          className="text-gray-600 text-xs font-thin cursor-pointer hover:underline"
        >
          [{children}]
        </button>
      ),

      h3: ({ children }: { children?: ReactNode }) => {
        const { number, newChildren } = extractLeadingNumber(children);
        return (
          <h3 className="mb-2 leading-relaxed text-xl font-bold text-black flex items-center">
            <span className="mr-2 ">
              {number ? `${number}.` : <FaArrowRight className="mr-1" />}
            </span>
            {newChildren}
          </h3>
        );
      },

      h1: ({ children }: { children?: ReactNode }) => (
        <h1 className="mb-2 leading-relaxed font-bold">{children}</h1>
      ),
      h2: ({ children }: { children?: ReactNode }) => (
        <h2 className="mb-2 leading-relaxed font-bold">{children}</h2>
      ),
      h4: ({ children }: { children?: ReactNode }) => (
        <h4 className="mb-2 leading-relaxed font-bold">{children}</h4>
      ),
      h5: ({ children }: { children?: ReactNode }) => (
        <h5 className="mb-2 leading-relaxed font-bold">{children}</h5>
      ),
      h6: ({ children }: { children?: ReactNode }) => (
        <h6 className="mb-2 leading-relaxed font-bold">{children}</h6>
      ),
      p: ({ children }: { children?: ReactNode }) => (
        <p className="mb-2 leading-relaxed text-gray-800">{children}</p>
      ),

      ul: ({ children }: { children?: ReactNode }) => (
        <ul className="list-[upper-alpha]  space-y-2 pl-4 mb-4 text-gray-800">{children}</ul>
      ),

      ol: ({ children }: { children?: ReactNode }) => (
        <ol className="list-decimal  space-y-2 pl-4 text-gray-800">{children}</ol>
      ),

      li: ({ children }: { children?: ReactNode }) => <li className="ml-2 list-disc">{children}</li>,

      table: ({ children }: { children?: ReactNode }) => (
        <div className="overflow-x-auto my-6 shadow-md rounded-lg">
          <table className="min-w-full border border-gray-200 text-sm bg-white rounded-lg">{children}</table>
        </div>
      ),

      thead: ({ children }: { children?: ReactNode }) => (
        <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b text-gray-700">{children}</thead>
      ),

      tbody: ({ children }: { children?: ReactNode }) => (
        <tbody className="divide-y divide-gray-200">{children}</tbody>
      ),

      tr: ({ children }: { children?: ReactNode }) => (
        <tr className="hover:bg-gray-100 transition-colors duration-150 ease-in-out cursor-pointer">{children}</tr>
      ),

      th: ({ children }: { children?: ReactNode }) => (
        <th className="px-6 py-3 border-r last:border-0 text-left font-semibold text-gray-800 uppercase text-xs tracking-wider">
          {children}
        </th>
      ),

      td: ({ children }: { children?: ReactNode }) => (
        <td className="px-6 py-3 border-r last:border-0 text-gray-700 whitespace-nowrap">{children}</td>
      ),
    }),
    [handleSourceClick]
  );
   // Parse charts from markdown
  const chartBlocks = useMemo(() => parseChartMarkdown(processedMarkdown), [processedMarkdown]);

  let lastIdx = 0;
  const parts: ReactNode[] = [];

  chartBlocks.forEach((block, idx) => {
    if (block.start > lastIdx) {
      parts.push(
        <ReactMarkdown
          key={`md-before-${idx}`}
          remarkPlugins={[remarkGfm]}
          components={components}
        >
          {processedMarkdown.slice(lastIdx, block.start)}
        </ReactMarkdown>
      );
    }
    parts.push(
      <div key={`chart-${idx}`} className="my-8">
        <ChartRenderer type={block.type} data={block.data} />
      </div>
    );
    lastIdx = block.end;
  });

  if (lastIdx < processedMarkdown.length) {
    parts.push(
      <ReactMarkdown
        key="md-after"
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {processedMarkdown.slice(lastIdx)}
      </ReactMarkdown>
    );
  }

  return <>{parts}</>;
};


//   return <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{processedMarkdown}</ReactMarkdown>;
// };

export default ChatMarkdown;
